import { toast } from 'react-hot-toast';

export interface WahaConfig {
    baseUrl: string;
    apiKey?: string;
}

export interface WahaSession {
    id: string;
    name: string;
    status: string;
}

export interface SendTextRequest {
    session: string;
    chatId: string;
    text: string;
    reply_to?: string;
    linkPreview?: boolean;
}

export interface SendMediaRequest {
    session: string;
    chatId: string;
    text?: string; // Caption
    file: {
        mimetype: string;
        filename: string;
        url?: string;
        data?: string; // Base64
        convert?: boolean; // For video
    };
    reply_to?: string;
}

// Webpack/CRA compatible env var access
const DEFAULT_BASE_URL = process.env.REACT_APP_WAHA_BASE_URL || 'http://localhost:3000';

class WhatsappService {
    private config: WahaConfig = {
        baseUrl: DEFAULT_BASE_URL,
        apiKey: ''
    };

    /**
     * Update configuration dynamically (e.g. from Settings UI)
     */
    configure(config: Partial<WahaConfig>) {
        this.config = { ...this.config, ...config };
    }

    private getHeaders(method: string) {
        const headers: Record<string, string> = {
            'Accept': 'application/json',
        };

        // Content-Type is needed for body data
        if (method !== 'GET' && method !== 'HEAD') {
            headers['Content-Type'] = 'application/json';
        }

        if (this.config.apiKey) {
            headers['X-Api-Key'] = this.config.apiKey;
        }
        return headers;
    }

    private async request<T>(endpoint: string, method: string = 'GET', body?: any): Promise<T> {
        const url = `${this.config.baseUrl}${endpoint}`;
        const headers = this.getHeaders(method);

        // Debug Log
        console.log(`[WAHA] ${method} ${url}`, { headers, body });

        try {
            const response = await fetch(url, {
                method,
                headers,
                body: body ? JSON.stringify(body) : undefined,
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Unauthorized: Invalid API Key. Please check your settings.');
                }
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.message || errorData.detail || `Request failed with status ${response.status}`;
                console.error(`WAHA API Error [${method} ${url}]:`, errorData);
                throw new Error(errorMessage);
            }

            return await response.json();
        } catch (error: any) {
            console.error(`WAHA Service Error:`, error);
            // Simplify error message for UI
            let uiMessage = error.message;
            if (error.message?.includes('Failed to fetch')) {
                uiMessage = 'Could not connect to WAHA server. Check URL.';
            }
            throw new Error(uiMessage);
        }
    }

    /**
     * 1. List all sections / sessions
     */
    async listSessions(): Promise<WahaSession[]> {
        return this.request<WahaSession[]>('/api/sessions');
    }

    /**
     * 2. Send Text Message
     */
    async sendText(req: SendTextRequest): Promise<any> {
        const { session, chatId, text, reply_to, linkPreview } = req;

        // Validate chatId format
        if (!chatId.endsWith('@c.us') && !chatId.endsWith('@g.us')) {
            console.warn('ChatID should usually end with @c.us or @g.us');
        }

        return this.request('/api/sendText', 'POST', {
            session,
            chatId,
            text,
            reply_to,
            linkPreview
        });
    }

    /**
     * 3. Send Image
     */
    /**
     * 3. Send Image
     */
    async sendImage(req: SendMediaRequest): Promise<any> {
        const payload = {
            session: req.session,
            chatId: req.chatId,
            file: req.file,
            caption: req.text, // Map text to caption
            reply_to: req.reply_to
        };
        return this.request('/api/sendImage', 'POST', payload);
    }

    /**
     * 4. Send Video
     */
    async sendVideo(req: SendMediaRequest): Promise<any> {
        // Ensure convert is true if not specified
        if (req.file.convert === undefined) {
            req.file.convert = true;
        }

        const payload = {
            session: req.session,
            chatId: req.chatId,
            file: req.file,
            caption: req.text, // Map text to caption
            reply_to: req.reply_to
        };
        return this.request('/api/sendVideo', 'POST', payload);
    }

    /**
     * 5. Send File (Document)
     */
    async sendFile(req: SendMediaRequest): Promise<any> {
        const payload = {
            session: req.session,
            chatId: req.chatId,
            file: req.file,
            caption: req.text, // Map text to caption
            reply_to: req.reply_to
        };
        return this.request('/api/sendFile', 'POST', payload);
    }
}

export const whatsappService = new WhatsappService();
